"use client";

import { useState, type ReactNode } from "react";
import Link from "next/link";
import { Lock } from "lucide-react";
import { LazyUpgradeQuoteModal } from "@/components/billing/LazyUpgradeQuoteModal";
import type { UpgradeQuoteSource } from "@/components/billing/UpgradeQuoteModal";
import { useAuth } from "@/contexts/auth-context";
import { diamondUpgradeNote } from "@/lib/billing/diamond-gap";
import { planSubscriptionFromRow, routePurchase, type PlanChangeRoute } from "@/lib/billing/purchase-route";
import { DIAMOND_MONTHLY_PRICE_ID } from "@/lib/billing/tier-price-ids";
import { canOpenUpgradeFlowClient } from "@/lib/billing/upgrade-flow-config";

export type DiamondDestination = "upgrade_modal" | "fix_billing" | "pricing";

export const DIAMOND_UPGRADE_CLASS = "tap inline-flex items-center justify-center gap-2 rounded-lg bg-zinc-50 px-5 text-[14.5px] font-semibold text-zinc-950 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

export type DiamondUpgrade = {
  readonly destination: DiamondDestination;
  readonly note: string | null;
  readonly start: () => void;
  readonly modal: ReactNode;
};

export function useDiamondUpgrade(source: Extract<UpgradeQuoteSource, "race_to_place_gate" | "whales_gate" | "sniper_preview">, shown: boolean): DiamondUpgrade {
  const { user, subscriptions } = useAuth();
  const [armed, setArmed] = useState(false);
  const [target, setTarget] = useState<PlanChangeRoute | null>(null);
  if (shown && !armed) setArmed(true);
  const plans = subscriptions.map(planSubscriptionFromRow);
  const route = routePurchase(plans, { priceId: DIAMOND_MONTHLY_PRICE_ID, interval: "month" });
  const planChange = route.kind === "plan_change" && canOpenUpgradeFlowClient(user) ? route : null;
  const modalRoute = target ?? (armed ? planChange : null);
  const destination: DiamondDestination = planChange ? "upgrade_modal" : route.kind === "fix_billing" ? "fix_billing" : "pricing";
  return {
    destination,
    note: diamondUpgradeNote(route, plans),
    start: () => setTarget(planChange),
    modal: modalRoute && (
      <LazyUpgradeQuoteModal
        open={target !== null}
        targetTier={modalRoute.targetTier}
        targetInterval={modalRoute.targetInterval}
        source={source}
        onClose={(result) => {
          setTarget(null);
          if (result.type === "success") window.location.reload();
        }}
      />
    ),
  };
}

export function ComesWithDiamond() {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-violet-500/[0.18] text-violet-300"><Lock size={13} aria-hidden="true" /></span>
      <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-zinc-400">Comes with Diamond</span>
    </div>
  );
}

export function DiamondUnlockAction({ upgrade, className, onChoose, children }: {
  upgrade: DiamondUpgrade;
  className: string;
  onChoose: (destination: DiamondDestination) => void;
  children: string;
}) {
  const label = <><Lock size={14} aria-hidden="true" />{children}</>;
  if (upgrade.destination === "upgrade_modal") {
    return <button type="button" onClick={() => { onChoose("upgrade_modal"); upgrade.start(); }} className={className}>{label}</button>;
  }
  const { destination } = upgrade;
  return <Link href={destination === "fix_billing" ? "/fix-billing" : "/pricing"} onClick={() => onChoose(destination)} className={className}>{label}</Link>;
}
